// Canvas drawing — boxes, danger zone, alert banner, HUD.
// Mirrors the visual contract of overlay.py so the two implementations look
// the same to a reviewer.

import type { Detection } from "../inference/types";

export interface OverlayHud {
  fps: number;
  detectionCount: number;
  trackCount: number;
  longestSeconds: number;
  alertCount: number;
  inferenceMs: number;
  showHud: boolean;
  showTrackIds: boolean;
  showConfidence: boolean;
}

export interface OverlayDangerZone {
  polygon: { x: number; y: number }[]; // pixel coords
  alertActive: boolean;
}

export class Overlay {
  constructor(private canvas: HTMLCanvasElement) {}

  resizeTo(w: number, h: number): void {
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  draw(opts: {
    detections: Detection[];
    danger?: OverlayDangerZone;
    alertBanner?: string;
    hud: OverlayHud;
    zoneHint?: string;
  }): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (opts.danger) this.drawDangerZone(ctx, opts.danger);
    this.drawDetections(ctx, opts.detections, opts.hud);
    if (opts.alertBanner) this.drawBanner(ctx, opts.alertBanner);
    if (opts.zoneHint) this.drawZoneHint(ctx, opts.zoneHint);
    if (opts.hud.showHud) this.drawHud(ctx, opts.hud);
  }

  private drawZoneHint(ctx: CanvasRenderingContext2D, text: string): void {
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.font = "16px -apple-system, system-ui, sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    const padding = 14;
    const tw = ctx.measureText(text).width;
    const x = w / 2;
    const y = h - 60;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(x - tw / 2 - padding, y - 16, tw + padding * 2, 32);
    ctx.fillStyle = "#fff";
    ctx.fillText(text, x, y);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "start";
  }

  private drawDangerZone(ctx: CanvasRenderingContext2D, dz: OverlayDangerZone): void {
    if (dz.polygon.length < 3) {
      // Show vertices the user has clicked so far — even if not a polygon yet.
      ctx.fillStyle = dz.alertActive ? "rgba(255,0,0,0.9)" : "rgba(0,200,255,0.9)";
      for (const p of dz.polygon) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    const stroke = dz.alertActive ? "rgba(255,80,80,0.95)" : "rgba(0,200,255,0.95)";
    const fill = dz.alertActive ? "rgba(255,0,0,0.18)" : "rgba(0,200,255,0.18)";
    ctx.beginPath();
    const first = dz.polygon[0]!;
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < dz.polygon.length; i++) {
      const pt = dz.polygon[i]!;
      ctx.lineTo(pt.x, pt.y);
    }
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.stroke();
    // Vertex handles
    ctx.fillStyle = stroke;
    for (const p of dz.polygon) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawDetections(
    ctx: CanvasRenderingContext2D,
    dets: Detection[],
    hud: OverlayHud,
  ): void {
    ctx.font = "13px ui-monospace, SFMono-Regular, monospace";
    ctx.lineWidth = 2;
    for (const d of dets) {
      const color =
        d.track_id !== null ? colorForTrack(d.track_id) : colorForClass(d.cls_id);
      const { x1, y1, x2, y2 } = d.bbox;
      ctx.strokeStyle = color;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      const label = formatLabel(d, hud);
      const textWidth = ctx.measureText(label).width;
      const labelH = 16;
      const labelY = y1 - 4 < labelH ? y1 + labelH : y1 - 4;
      ctx.fillStyle = color;
      ctx.fillRect(x1, labelY - labelH, textWidth + 8, labelH);
      ctx.fillStyle = "#000";
      ctx.fillText(label, x1 + 4, labelY - 3);
    }
  }

  private drawBanner(ctx: CanvasRenderingContext2D, text: string): void {
    const w = this.canvas.width;
    const h = 40;
    ctx.fillStyle = "rgba(200,0,0,0.9)";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "#fff";
    ctx.font = "bold 16px -apple-system, system-ui, sans-serif";
    const tw = ctx.measureText(text).width;
    ctx.fillText(text, Math.max(8, (w - tw) / 2), 26);
  }

  private drawHud(ctx: CanvasRenderingContext2D, hud: OverlayHud): void {
    const padding = 8;
    const lines = [
      `${hud.fps.toFixed(1)} fps   inf ${hud.inferenceMs.toFixed(0)}ms`,
      `det ${hud.detectionCount}   tracks ${hud.trackCount}` +
        (hud.longestSeconds > 0 ? `   longest ${hud.longestSeconds.toFixed(1)}s` : ""),
      `alerts ${hud.alertCount}`,
    ];
    ctx.font = "12px ui-monospace, SFMono-Regular, monospace";
    ctx.textBaseline = "top";
    let y = padding;
    for (const line of lines) {
      // Shadow for legibility on bright backgrounds
      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.fillText(line, padding + 1, y + 1);
      ctx.fillStyle = "#fff";
      ctx.fillText(line, padding, y);
      y += 16;
    }
    ctx.textBaseline = "alphabetic";
  }
}

function formatLabel(d: Detection, hud: OverlayHud): string {
  const parts: string[] = [d.cls_name];
  if (hud.showTrackIds && d.track_id !== null) parts.push(`#${d.track_id}`);
  if (hud.showConfidence) parts.push(d.confidence.toFixed(2));
  if (d.seen_for_seconds !== null && d.seen_for_seconds > 0) {
    parts.push(`(${d.seen_for_seconds.toFixed(1)}s)`);
  }
  return parts.join(" ");
}

function colorForTrack(trackId: number): string {
  // Distinct, vivid hues per track id.
  const hue = (trackId * 73) % 360;
  return `hsl(${hue}, 80%, 55%)`;
}

function colorForClass(clsId: number): string {
  const hue = (clsId * 137) % 360;
  return `hsl(${hue}, 60%, 60%)`;
}
